import React from 'react';
import dayjs from 'dayjs';
import { useTranslation } from 'react-i18next';
import type { Calendar } from '@services/requests/housien-models/Calendar';
import type { ExpandedEvent } from '@features/agenda/utils/expand-recurrence';
import { isSameDay } from '@features/agenda/utils/agenda-dates';
import { eventColor } from '../event-color';
import { AgendaEventRow } from './AgendaEventRow';
import { calendarDisplayName } from '@features/agenda/utils/calendar-display';
import { Text } from '@/shared/ui';

interface MobileListViewProps {
  anchor: Date;
  events: ExpandedEvent[];
  calendars: Calendar[];
  onSelect: (event: ExpandedEvent) => void;
}

/**
 * Chronological list of upcoming events grouped by day. Primary mobile view
 * optimised for readability and one-thumb reach.
 *
 * @param props Anchor date, events, calendars and select handler.
 * @returns Rendered mobile list view.
 */
export const MobileListView: React.FC<MobileListViewProps> = ({
  anchor,
  events,
  calendars,
  onSelect,
}) => {
  const { t } = useTranslation('features/agenda');
  const days = Array.from({ length: 7 }, (_, i) =>
    dayjs(anchor).add(i, 'day').toDate()
  );

  return (
    <div
      data-testid="agenda-mobile-list"
      className="h-full overflow-y-auto px-(--space-4) pt-(--space-3)"
      style={{ paddingBottom: 'calc(var(--space-16) + env(safe-area-inset-bottom, 0px))' }}
    >
      {days.map((day) => {
        const dayEvents = events
          .filter((e) => isSameDay(e.start_at_utc, day))
          .sort((a, b) => a.start_at_utc.localeCompare(b.start_at_utc));
        if (dayEvents.length === 0) return null;
        const today = dayjs().isSame(dayjs(day), 'day');
        return (
          <div key={dayjs(day).format('YYYY-MM-DD')} className="mb-(--space-5)">
            <DayHeader day={day} isToday={today} t={t} />
            <div
              /* No timeline rail and no indent: the rows already carry their
                 own times and colour bars, and the rail spent 40px of a 400px
                 screen repeating what they say. */
              className="flex flex-col gap-(--space-2)"
            >
              {dayEvents.map((ev) => (
                <EventCard
                  key={`${ev.event_id}-${ev.occurrence_start}`}
                  event={ev}
                  calendars={calendars}
                  t={t}
                  onClick={() => onSelect(ev)}
                />
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
};

/**
 * @description Renders the header for a day section in the list view.
 */
/**
 * The day a run of appointments belongs to.
 *
 * One line, in the same words the day screen's own line uses — "Hoje · qui,
 * 24 de setembro". It used to be a 40px wine disc beside two stacked lines,
 * which made each day heavier than the appointments under it and read as a
 * different product from the day screen one tap away.
 */
function DayHeader({ day, isToday, t }: { day: Date; isToday: boolean; t: (k: string) => string }) {
  const d = dayjs(day);
  return (
    <Text
      component="h3"
      data-testid="agenda-list-day-header"
      className="mb-(--space-2) text-(length:--text-body-sm) text-[var(--text-secondary)]"
      style={{ fontWeight: 'var(--font-semibold)' as unknown as number }}
    >
      {[isToday ? t('mobile.today') : null, d.format('ddd, D [de] MMMM')].filter(Boolean).join(' · ')}
    </Text>
  );
}

interface EventCardProps {
  event: ExpandedEvent;
  calendars: Calendar[];
  t: (k: string, opts?: Record<string, unknown>) => string;
  onClick: () => void;
}
/**
 * @description Renders an individual event card in the list.
 */

function EventCard({ event, calendars, t, onClick }: EventCardProps) {
  const calendar = calendars.find((c) => c.calendar_id === event.calendar_id);
  const calName = calendar ? calendarDisplayName(calendar, t) : '';
  const isRecurring = Array.isArray(event.recurrence) && event.recurrence.length > 0;
  const meta = [calName, isRecurring ? t('preview.recurring') : ''].filter(Boolean).join(' · ');

  return (
    <AgendaEventRow
      event={event}
      color={eventColor(event, calendars)}
      meta={meta || undefined}
      onClick={onClick}
    />
  );
}
